import { CpfInput } from '@/_components/inputs/cpf-input'
import { PhoneInput } from '@/_components/inputs/phone-input'
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
} from '@/_components/ui'
import type { TUpdatePersonForm } from '@/_schemas/update-person'
import { ContentContainer } from '@/features/_components/content-container'
import { InputGroup } from '@/features/_components/input-group'
import { Text, Title } from '@/features/_components/ui/typography'
import { getStatusOptions } from '@/features/_constants/admission-status'
import type { AuthSession, TUserType } from '@/features/_lib/auth-client.types'
import { mutations } from '@/features/_lib/react-query'
import type { TExposedApplicationAggregateDTO } from '@doutorsim/shared/applications'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Pencil, X } from 'lucide-react'
import { useContext, useRef, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { CiCircleAlert } from 'react-icons/ci'
import { toast } from 'sonner'
import { AccessControl } from '../../../../_components/access-control'
import { FacilitySelect } from '../../../_components/facility-select'
import { EditContext } from '../../_providers/edit'
import { ApprovedAmountCard } from './approved-amount-card'
import { ConfirmedDeclarationsCard } from './confirmed-declarations-card'
import { EventSteps } from './event-steps'

interface UpdatePersonFormProps {
  user: AuthSession['user']
  userType: TUserType
  application: TExposedApplicationAggregateDTO
}

export const UpdatePersonForm = ({ user, userType, application }: UpdatePersonFormProps) => {
  const currentCompanyId = application.companyId

  const isArchived = application?.status === 'rejected'

  const hasApprovalData = Boolean(application.proposal?.disbursementAmount)

  const form = useFormContext<TUpdatePersonForm>()
  const { isFormEditable, isCancelDialogOpen, setIsCancelDialogOpen, setIsFormEditable } =
    useContext(EditContext)

  const formWasModified = Object.keys(form.formState.dirtyFields).length > 0

  const resetForm = () => {
    form.reset()
    setIsCancelDialogOpen(false)
    setIsFormEditable(false)
  }

  const handleCancelClick = () => setIsCancelDialogOpen(true)

  const formRef = useRef<HTMLFormElement | null>(null)
  const [isPending, setIsPending] = useState(false)

  const queryClient = useQueryClient()
  const updatePersonMutation = useMutation(mutations.persons.update(queryClient))
  const updateStatusMutation = useMutation(mutations.staffApplications.updateStatus(queryClient))

  const onSubmit = async (formData: TUpdatePersonForm) => {
    setIsPending(true)

    try {
      const updatePersonInput = {
        id: application.personId,
        companyId: currentCompanyId,
        name: formData.application.person.name,
        cpf: formData.application.person.cpf,
        email: formData.application.person.email ?? null,
        phone: formData.application.person.phone ?? null,
        birthDate: null,
        motherName: null,
        estimatedIncome: null,
        address: null,
      }

      // Update person data
      await updatePersonMutation.mutateAsync(updatePersonInput)

      // Update application status if changed
      if (formData.application.status !== application.status) {
        await updateStatusMutation.mutateAsync({
          applicationId: application.id,
          status: formData.application.status,
        })
      }

      setIsFormEditable(false)
      formRef.current?.scrollIntoView({ behavior: 'smooth' })
      toast.success('Paciente atualizado com sucesso!')
    } catch {
      toast.error('Erro ao atualizar paciente.', {
        description: 'Algo deu errado. Por favor, tente novamente.',
      })
    } finally {
      setIsPending(false)
    }
  }

  const buttonDisabledStates = !formWasModified || isPending || !isFormEditable || isArchived

  return (
    <div className="flex min-h-0 w-full flex-1 flex-col">
      <Dialog open={isCancelDialogOpen} onOpenChange={setIsCancelDialogOpen}>
        <DialogContent className="min-w-[336px] max-w-[389px] p-5">
          <div className="flex flex-col gap-5">
            <div className="flex flex-col gap-2">
              <div className="flex items-start gap-1">
                <CiCircleAlert size={24} />
                <Title className="text-base">Há alterações não salvas!</Title>
              </div>
              <Text>Ao prosseguir, essas alterações serão perdidas. Deseja continuar?</Text>
            </div>

            <div className="flex gap-3 self-end">
              <DialogClose asChild>
                <Button variant="gray" className="rounded-md font-bold">
                  Cancelar
                </Button>
              </DialogClose>
              <Button
                variant="destructive"
                className="min-w-[80px] text-sm font-bold"
                onClick={resetForm}
              >
                Continuar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Form {...form}>
        <form
          className="flex flex-1 flex-col overflow-hidden"
          onSubmit={form.handleSubmit(onSubmit)}
          ref={formRef}
        >
          <div className="scrollbar flex-1 overflow-y-auto">
            <div className="flex gap-5">
              <div className="flex w-full flex-col gap-5">
                <ContentContainer>
                  <div className="flex items-center justify-between">
                    <h2 className="font-bold">Identificação</h2>
                    {user.userType === 'staff' && (
                      <button
                        type="button"
                        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                        onClick={() => {
                          if (isFormEditable && formWasModified) {
                            return setIsCancelDialogOpen(true)
                          }
                          setIsFormEditable(!isFormEditable)
                        }}
                      >
                        {isFormEditable ? (
                          <>
                            <X size={12} />
                            Cancelar
                          </>
                        ) : (
                          <>
                            <Pencil size={12} />
                            Editar
                          </>
                        )}
                      </button>
                    )}
                  </div>
                  <InputGroup>
                    <FormField
                      control={form.control}
                      name="application.person.name"
                      render={({ field }) => (
                        <FormItem className="w-1/2">
                          <FormLabel>Nome</FormLabel>
                          <FormControl>
                            <Input placeholder="Digite o nome" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="application.person.cpf"
                      render={({ field }) => (
                        <FormItem className="w-1/2">
                          <FormLabel>CPF</FormLabel>
                          <FormControl>
                            <CpfInput placeholder="Digite o CPF" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </InputGroup>

                  <InputGroup>
                    <FormField
                      control={form.control}
                      name="application.person.email"
                      render={({ field }) => (
                        <FormItem className="w-1/2">
                          <FormLabel>E-mail</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="Digite o e-mail"
                              {...field}
                              value={field.value ?? ''}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="application.person.phone"
                      render={({ field }) => (
                        <FormItem className="w-1/2">
                          <FormLabel>Telefone</FormLabel>
                          <FormControl>
                            <PhoneInput
                              onChange={field.onChange}
                              value={field.value ?? ''}
                              getInputRef={field.ref}
                              disabled={field.disabled}
                              onBlur={field.onBlur}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </InputGroup>

                  <AccessControl
                    role={user.userType}
                    renderIfAdmin={
                      <>
                        <Separator className="bg-brand-grayscale-3" />
                        <InputGroup>
                          <FormField
                            control={form.control}
                            name="application.facility"
                            render={({ field }) => (
                              <FormItem className="flex w-1/2 flex-col">
                                <FormLabel>Instituição</FormLabel>
                                <FacilitySelect
                                  onChange={field.onChange}
                                  selectedFacility={field.value}
                                  resetField={form.resetField}
                                  isLoading={isArchived || !isFormEditable}
                                  popoverWidth={600}
                                  disabled={true}
                                />
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name="application.status"
                            render={({ field }) => (
                              <FormItem className="flex w-1/2 flex-col">
                                <FormLabel>Status de Admissão</FormLabel>
                                <Select
                                  onValueChange={field.onChange}
                                  value={field.value}
                                  disabled={field.disabled}
                                >
                                  <FormControl>
                                    <SelectTrigger>
                                      <SelectValue placeholder="Selecione o status de admissão" />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    {getStatusOptions(userType).map((option) => (
                                      <SelectItem key={option.value} value={option.value}>
                                        {option.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </InputGroup>
                      </>
                    }
                    renderIfUser={null}
                  />
                </ContentContainer>

                <p className="flex h-1 items-center px-5 text-sm opacity-50">
                  Criado em{' '}
                  {application.person.createdAt
                    ? new Date(application.person.createdAt).toLocaleDateString('pt-BR', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                      })
                    : '-'}
                </p>

                <AccessControl
                  role={user.userType}
                  renderIfAdmin={<ConfirmedDeclarationsCard application={application} />}
                  renderIfUser={null}
                />
              </div>

              <AccessControl
                role={user.userType}
                renderIfAdmin={
                  <div className="flex w-[30%] shrink-0 flex-col gap-[10px]">
                    {hasApprovalData && (
                      <ContentContainer className="gap-4 px-10 py-5">
                        <ApprovedAmountCard application={application} />
                      </ContentContainer>
                    )}

                    <ContentContainer className="overflow-y-auto">
                      <h2 className="font-bold">Progresso do paciente</h2>
                      <div className="scrollbar space-y-5 overflow-y-auto">
                        <EventSteps application={application} userType={userType} />
                      </div>
                    </ContentContainer>
                  </div>
                }
                renderIfUser={null}
              />
            </div>
          </div>

          <AccessControl
            role={user.userType}
            renderIfAdmin={
              <div className="flex shrink-0 justify-end gap-[10px] pt-4">
                <Button
                  variant="gray"
                  className="font-bold"
                  type="button"
                  disabled={buttonDisabledStates}
                  onClick={handleCancelClick}
                >
                  Cancelar
                </Button>
                <Button type="submit" isLoading={isPending} disabled={buttonDisabledStates}>
                  Salvar
                </Button>
              </div>
            }
            renderIfUser={null}
          />
        </form>
      </Form>
    </div>
  )
}
